// Загрузка файлов через media-pipeline поверх transport.rest (SPEC §4.2 composer-вложения, §4.5 голос).
// init → chunked PUT (Content-Range) → complete → Attachment. Прогресс 0..1 (FR-MEDIA-07) и отмена по
// AbortSignal. Это ЕДИНСТВЕННЫЙ путь загрузки — идёт через транспорт (инв.2), своего fetch нет.
//
// Здесь же — адаптер MediaUploadPort для voice-recorder (createVoiceRecorder ждёт `uploadAudio`,
// voice-recorder/ports.ts): сводим тот же init/put/complete в один шаг.
//
// UPQ-1 (из форка «Наш.Чат» 03.11.2026): устойчивость к мобильной сети. Прод 04.10: 13 фото/видео с
// Android — ни один кусок не дошёл до сворачивания приложения, все упали насовсем; 18.10: видео 21 МБ
// оборвалось на 16 МБ. Теперь переходящий сбой шага (сеть, таймаут, 5xx, 429) не роняет загрузку:
// ждём «можно продолжать» (страница видима и есть сеть) + бэкоф и повторяем ТОТ ЖЕ шаг тем же uploadId —
// докачка с места обрыва (сервер принимает повтор части идемпотентно: partNumber из offset, S3
// перезаписывает часть). Сессия собрана GC/неизвестна (NOT_FOUND) — один раз начинаем заново.
// Непереходящие ошибки (VALIDATION/PAYLOAD_TOO_LARGE/FORBIDDEN/…) и отмена — сразу наружу.

import type { Attachment, ChatId } from '@tele-chat/shared-contracts'
import { TransportError, type Transport } from '@tele-chat/client-transport'
import type {
  MediaUploadPort,
  AudioUploadMeta,
  UploadAudioOptions,
} from '@tele-chat/voice-recorder'

/** Размер части chunked-загрузки (1 МБ) — баланс прогресса/числа запросов; сервер докачивает по offset. */
export const UPLOAD_CHUNK_BYTES = 1024 * 1024

/** Потолок прогресса до ответа complete: все байты ушли, но вложение ещё собирается на сервере. */
export const UPLOAD_PROGRESS_CAP = 0.99

/** Паузы перед повтором одного и того же шага (мс); дальше повторяется последняя. */
export const UPLOAD_RETRY_BACKOFF_MS: readonly number[] = [2_000, 5_000, 15_000, 30_000]

/** Сколько раз подряд можно споткнуться на одном шаге (при видимой странице и живой сети). */
export const UPLOAD_MAX_ATTEMPTS = 8

/** После возврата приложения/сети — короткая пауза: радио и сокеты поднимаются не мгновенно. */
export const UPLOAD_RESUME_SETTLE_MS = 1_000

/** Потолок ожидания по Retry-After (429), чтобы кривой заголовок не подвесил загрузку надолго. */
export const UPLOAD_MAX_RETRY_AFTER_MS = 120_000

/** Фаза загрузки для UI: идут байты / ждём сеть (сбой, пауза перед повтором, свёрнутое приложение). */
export type UploadPhase = 'uploading' | 'waiting'

/** Снимок для докачки: живая сессия и offset первого НЕподтверждённого куска. */
export interface UploadResumeState {
  uploadId: string
  partSize: number
  offset: number
}

/**
 * «Можно продолжать»: страница видима и сеть есть. Порт — чтобы тесты управляли им явно.
 * subscribe зовёт колбэк на любом изменении (visibility/online/offline); возвращает отписку.
 */
export interface ResumeGate {
  canProceed(): boolean
  subscribe(onChange: () => void): () => void
}

export interface UploadRetryPolicy {
  backoffMs: readonly number[]
  maxAttempts: number
  settleMs: number
  gate: ResumeGate
  /** Пауза, прерываемая signal (отмена → AbortError). */
  sleep: (ms: number, signal?: AbortSignal) => Promise<void>
}

export interface UploadFileOptions {
  /** Прогресс 0..1 (доля переданных байт). */
  onProgress?: (fraction: number) => void
  /** Отмена незавершённой загрузки (рвёт и запрос в полёте, и ожидание повтора). */
  signal?: AbortSignal
  /** Смена фазы (UPQ-1): 'waiting' — сбой, ждём сеть/видимость/паузу; 'uploading' — снова шлём. */
  onPhase?: (phase: UploadPhase) => void
  /** Продолжить уже начатую сессию (повтор после ошибки) с её offset вместо нового init. */
  resume?: UploadResumeState
  /** Каждый подтверждённый шаг (init/кусок) — снимок для докачки; потребитель хранит его в слоте. */
  onCheckpoint?: (state: UploadResumeState) => void
  /** Политика повторов (тесты подменяют таймеры и «можно продолжать»). */
  retry?: Partial<UploadRetryPolicy>
}

/** Браузерный «можно продолжать»: document.visibilityState и navigator.onLine + их события. */
export function createBrowserResumeGate(): ResumeGate {
  return {
    canProceed(): boolean {
      const visible = typeof document === 'undefined' || document.visibilityState !== 'hidden'
      const online = typeof navigator === 'undefined' || navigator.onLine !== false
      return visible && online
    },
    subscribe(onChange: () => void): () => void {
      const hasDoc = typeof document !== 'undefined' && typeof document.addEventListener === 'function'
      const hasWin = typeof window !== 'undefined' && typeof window.addEventListener === 'function'
      if (hasDoc) document.addEventListener('visibilitychange', onChange)
      if (hasWin) {
        window.addEventListener('online', onChange)
        window.addEventListener('offline', onChange)
      }
      return () => {
        if (hasDoc) document.removeEventListener('visibilitychange', onChange)
        if (hasWin) {
          window.removeEventListener('online', onChange)
          window.removeEventListener('offline', onChange)
        }
      }
    },
  }
}

function abortError(): DOMException {
  return new DOMException('Загрузка отменена', 'AbortError')
}

/** Пауза на setTimeout, прерываемая signal. */
export function abortableSleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError())
      return
    }
    const onAbort = (): void => {
      clearTimeout(timer)
      reject(abortError())
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

/** Ждёт, пока gate разрешит продолжать (или отмены). */
function waitForGate(gate: ResumeGate, signal?: AbortSignal): Promise<void> {
  if (gate.canProceed()) return Promise.resolve()
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError())
      return
    }
    let off: () => void = () => {}
    const onAbort = (): void => {
      off()
      reject(abortError())
    }
    off = gate.subscribe(() => {
      if (!gate.canProceed()) return
      off()
      signal?.removeEventListener('abort', onAbort)
      resolve()
    })
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

type FailureKind = 'transient' | 'session-lost' | 'fatal'

/**
 * Классификация сбоя шага. Транспорт отдаёт сеть/таймаут/обрыв как INTERNAL БЕЗ HTTP-статуса, 5xx/502
 * прокси — INTERNAL со статусом; это переходящее. NOT_FOUND — сессии больше нет (GC/рестарт). Прочие
 * доменные коды (VALIDATION, PAYLOAD_TOO_LARGE, FORBIDDEN, UNAUTHENTICATED, CONFLICT) повтором не лечатся.
 */
export function classifyUploadFailure(err: unknown): FailureKind {
  if (err instanceof TransportError) {
    if (err.code === 'NOT_FOUND') return 'session-lost'
    if (err.code === 'RATE_LIMITED') return 'transient'
    if (err.code === 'INTERNAL') {
      const s = err.status
      if (s === undefined || s >= 500 || s === 408 || (s >= 200 && s < 300)) return 'transient'
      return 'fatal'
    }
    return 'fatal'
  }
  // Транспорт оборачивает ЛЮБОЙ сетевой сбой в TransportError (networkToError) — чужая ошибка
  // (AbortError, баг, сбой Blob.slice) повтором не лечится: наружу сразу, как раньше.
  return 'fatal'
}

/** Пауза по Retry-After (429), если сервер её назвал; иначе undefined. */
function retryAfterMs(err: unknown): number | undefined {
  if (!(err instanceof TransportError) || err.code !== 'RATE_LIMITED') return undefined
  const sec = err.details?.retryAfterSec
  if (typeof sec !== 'number' || !Number.isFinite(sec) || sec <= 0) return undefined
  return Math.min(sec * 1000, UPLOAD_MAX_RETRY_AFTER_MS)
}

/**
 * Загружает Blob/File одним вызовом: initUpload → последовательные putUploadChunk → completeUpload.
 * Возвращает готовый Attachment (его id идёт в SendMessageDTO.attachmentIds / voiceAttachmentId).
 * Переходящие сбои шага переживает сам (UPQ-1); наружу — отмена, непереходящая ошибка или исчерпанные
 * попытки (последняя ошибка шага).
 */
export async function uploadFile(
  rest: Transport['rest'],
  chatId: ChatId,
  file: Blob,
  meta: { filename: string; mime: string; durationMs?: number },
  opts: UploadFileOptions = {},
): Promise<Attachment> {
  const total = file.size
  const signal = opts.signal
  const policy: UploadRetryPolicy = {
    backoffMs: opts.retry?.backoffMs ?? UPLOAD_RETRY_BACKOFF_MS,
    maxAttempts: opts.retry?.maxAttempts ?? UPLOAD_MAX_ATTEMPTS,
    settleMs: opts.retry?.settleMs ?? UPLOAD_RESUME_SETTLE_MS,
    gate: opts.retry?.gate ?? createBrowserResumeGate(),
    sleep: opts.retry?.sleep ?? abortableSleep,
  }

  // Прогресс — по байтам, ушедшим в сеть, в том числе ВНУТРИ куска (UP-1: кусок 8 МиБ на мобильном
  // канале идёт десятки секунд, и полоска «висела» на 0%). До ответа complete держим не выше
  // UPLOAD_PROGRESS_CAP: «100%» — только когда вложение реально собрано на сервере. Внутри попытки —
  // монотонно; новая попытка куска шлёт его байты заново — полоска честно откатывается к НАЧАЛУ этого
  // куска (не к нулю): иначе при обрыве она «застывала» у 99% на всё время повторной отправки.
  let shown = 0
  const report = (sentBytes: number): void => {
    if (total === 0) return
    const f = Math.min(UPLOAD_PROGRESS_CAP, sentBytes / total)
    if (f <= shown && sentBytes !== 0) return
    shown = f
    opts.onProgress?.(f)
  }
  /** Начало попытки куска с offset `fromBytes`: полоска не выше уже подтверждённого. */
  const rewindTo = (fromBytes: number): void => {
    if (total === 0) return
    const f = Math.min(UPLOAD_PROGRESS_CAP, fromBytes / total)
    if (f >= shown) return
    shown = f
    opts.onProgress?.(f)
  }

  let phase: UploadPhase | null = null
  const setPhase = (p: UploadPhase): void => {
    if (phase === p) return
    phase = p
    opts.onPhase?.(p)
  }
  setPhase('uploading')

  const throwIfAborted = (): void => {
    if (signal?.aborted) throw abortError()
  }

  /**
   * Один шаг с повторами переходящих сбоев. Сбой при свёрнутом приложении/без сети попыткой не считается
   * (виновато не сервер: фон замораживает запросы). session-lost и fatal — наружу как есть.
   */
  async function step<T>(run: () => Promise<T>): Promise<T> {
    let attempt = 0
    for (;;) {
      throwIfAborted()
      try {
        const out = await run()
        setPhase('uploading')
        return out
      } catch (err) {
        if (signal?.aborted) throw err // отмена пользователем — без повторов, ошибка как была
        if (classifyUploadFailure(err) !== 'transient') throw err
        const gateWasOpen = policy.gate.canProceed()
        if (gateWasOpen) attempt += 1
        if (attempt >= policy.maxAttempts) throw err
        setPhase('waiting')
        if (gateWasOpen) {
          const base = policy.backoffMs[Math.min(Math.max(attempt - 1, 0), policy.backoffMs.length - 1)] ?? 0
          await policy.sleep(Math.max(base, retryAfterMs(err) ?? 0), signal)
          await waitForGate(policy.gate, signal)
        } else {
          await waitForGate(policy.gate, signal)
          await policy.sleep(policy.settleMs, signal)
        }
        throwIfAborted()
        setPhase('uploading')
      }
    }
  }

  // chatId обязателен на init/complete (шов R2 W7: media бакетит ключ по чату и проверяет владение).
  // Без него сервер отвечал 400 и загрузка вложения/картинки падала молча.
  const startSession = async (): Promise<UploadResumeState> => {
    const r = await step(() =>
      rest.initUpload({ filename: meta.filename, mime: meta.mime, size: total, chatId }, { signal }),
    )
    const state = { uploadId: r.uploadId, partSize: r.partSize, offset: 0 }
    opts.onCheckpoint?.(state)
    return state
  }

  let session: UploadResumeState = opts.resume ?? (await startSession())
  let restarted = false
  if (opts.resume !== undefined) report(Math.min(opts.resume.offset, total))

  for (;;) {
    try {
      const attachment = await sendFrom(session)
      opts.onProgress?.(1)
      return attachment
    } catch (err) {
      // Сессии на сервере больше нет (GC через сутки простоя, рестарт) — один раз заново с нуля.
      if (signal?.aborted || restarted || classifyUploadFailure(err) !== 'session-lost') throw err
      restarted = true
      shown = 0
      report(0)
      session = await startSession()
    }
  }

  async function sendFrom(s: UploadResumeState): Promise<Attachment> {
    const { uploadId } = s
    // Режем РОВНО по серверному partSize: сервер считает partNumber = floor(offset / partSize) + 1, а S3
    // дедуплицирует части по partNumber. Если чанк меньше partSize (раньше — фиксированные 1 MiB против
    // серверных 8 MiB), несколько чанков схлопывались в один partNumber и перезаписывали друг друга →
    // большой файл (фото) сохранялся усечённым и не открывался. UPLOAD_CHUNK_BYTES — фолбэк, если сервер
    // почему-то не прислал partSize (старый сервер).
    const chunkBytes = Number.isFinite(s.partSize) && s.partSize > 0 ? s.partSize : UPLOAD_CHUNK_BYTES
    // Докачка — строго с границы части (чекпойнт после куска кратен partSize; все байты ушли, но
    // complete сорвался — offset = total, сразу к complete).
    let offset = s.offset >= total ? total : Math.max(0, s.offset - (s.offset % chunkBytes))

    while (offset < total) {
      throwIfAborted()
      const end = Math.min(offset + chunkBytes, total)
      const chunk = file.slice(offset, end, meta.mime)
      const chunkStart = offset
      // Таймаут куска считает транспорт от его размера (UP-1); signal — чтобы «убрать вложение» рвало
      // и кусок в полёте, а не только следующий.
      await step(() => {
        rewindTo(chunkStart) // повтор куска — полоска с его начала
        return rest.putUploadChunk(uploadId, chunk, { start: chunkStart, end: end - 1, total }, {
          signal,
          onUploadProgress: (sent) => report(chunkStart + Math.min(sent, end - chunkStart)),
        })
      })
      offset = end
      report(offset)
      opts.onCheckpoint?.({ uploadId, partSize: s.partSize, offset })
    }

    throwIfAborted()
    // durationMs (голосовые): live-webm от MediaRecorder не несёт длительность в заголовке — без
    // заявки клиента сервер хранил 0 и плеер показывал «пустое» сообщение 0:00. Повтор complete
    // безопасен: сервер отдаёт уже собранное вложение по upload_id (идемпотентность §4.10).
    return step(() =>
      rest.completeUpload(
        uploadId,
        chatId,
        typeof meta.durationMs === 'number' && meta.durationMs > 0 ? { durationMs: meta.durationMs } : undefined,
        { signal },
      ),
    )
  }
}

/**
 * MediaUploadPort для voice-recorder (его createVoiceRecorder требует `uploadAudio`). Проекция того
 * же media-pipeline-пути через транспорт (voice-recorder/ports.ts §4.5).
 */
export function createMediaUploadPort(rest: Transport['rest'], chatId: ChatId): MediaUploadPort {
  return {
    uploadAudio(blob: Blob, meta: AudioUploadMeta, opts?: UploadAudioOptions): Promise<Attachment> {
      return uploadFile(
        rest,
        chatId,
        blob,
        { filename: meta.filename, mime: meta.mime, durationMs: meta.durationMs },
        { onProgress: opts?.onProgress, signal: opts?.signal },
      )
    },
  }
}
